import { BorderOutlined, CaretRightOutlined, CheckSquareOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Divider } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import BaseTable from '@components/BaseTable';
import ElementWithPermission from '@components/ElementWithPermission';
import LicenseModal from '@components/LicenseModal';
import ListPage from '@components/ListPage/ListPage';
import Loading from '@components/Loading/Loading';
import SearchFormLog from '@components/SearchFormLog';
import PageWrapper from '@components/PageWrapper/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import { showErrorMessage } from '@services/notifyService';
import { convertUtcToTimezone } from '@utils/datetimeHelper';
import { getEditPermissions } from '@utils/permission';
import styles from './index.module.scss';
import LogTable from './LogTable';
import { parserFood } from './parseFoodData';

// Port từ ra-log-monitor-cms/source/src/containers/log/LogManagementListPage.js (634 dòng) -> hook.
//
// Đặc thù giữ NGUYÊN:
//   · `getList()` CHỈ gọi API khi `search.posId` có giá trị -> chưa nhập ID thì bảng trống
//   · dữ liệu đơn hàng nằm trong `dataPayment`: base64 -> TextDecoder('utf-8') -> tách bằng regex
//   · bấm DÒNG -> modal "thông tin" (tổng tiền/tiền boa/giao dịch), bấm icon con mắt -> modal chi tiết
//   · nút Sửa gate [1]+[3]; `permissions[3] = 'not_have_update'` (chuỗi giả) -> LUÔN ẨN
//   · icon con mắt KHÔNG gate quyền
//   · tô nền dòng đang chọn bằng thao tác DOM trực tiếp (class `selected-row`)
//   · listener `visibilitychange`: quay lại tab thì nạp lại bảng
//   · tiền định dạng kiểu Đức: dấu ',' thập phân, ký hiệu €
//   · tiêu đề cột 'Cách trả ' CÓ khoảng trắng thừa ở cuối — chép đúng

const PAGE_PATH = '/logs';

// ===== Logic thuần, port nguyên văn =====
const numberWithCommas = (x) => x.toString().replace('.', ',');

const parseEuro = (value) => {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') return isNaN(value) ? null : value;
    let text = String(value).replace(/[^\d.,-]/g, '');
    if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
    const number = parseFloat(text);
    return isNaN(number) ? null : number;
};

const formatEuro = (value) => {
    const euro = parseEuro(value);
    return euro === null ? null : numberWithCommas(euro.toFixed(2));
};

const renderTips = (value) => {
    const euro = parseEuro(value);
    if (euro === null || euro <= 0) return <span>0 &euro;</span>;
    return (
        <span>
            {numberWithCommas(euro.toFixed(2))} &euro;
        </span>
    );
};

// nguồn decodeData: base64 -> utf-8 -> regex 5 nhóm; phần `tips` cắt từ sau dấu '::' CUỐI CÙNG
const decodeData = (data) => {
    if (data) {
        const regex = /([^:]+)::([^:]+)::([^:]+)::([^<]+)::(.*)/;
        const bytes = Uint8Array.from(atob(decodeURIComponent(data)), (c) => c.charCodeAt(0));
        const parsed = new TextDecoder('utf-8').decode(bytes);
        const match = parsed.match(regex);
        if (match) {
            let otherData = match[5];
            let tips = null;
            const tipsIndex = otherData.lastIndexOf('::');
            if (tipsIndex !== -1) {
                tips = otherData.slice(tipsIndex + 2);
                otherData = otherData.slice(0, tipsIndex);
            }
            return {
                isPrint: match[1] ? JSON.parse(match[1]) : null,
                deviceId: match[2],
                customerName: match[3],
                total: match[4],
                otherData,
                tips,
            };
        }
    }
    return {};
};

// nguồn parseInputString: không có '<==>' -> một giao dịch 'Bargeld'; có thì tách theo '<==>' rồi '<->'
const parseInputString = (inputString) => {
    if (!inputString.includes('<==>')) {
        const index = inputString.indexOf('<->') + 3;
        const value = inputString.slice(index);
        return { transaction: [{ name: 'Bargeld', value }] };
    }
    const dataArr = inputString.split('<==>');
    const transaction = [];
    dataArr.forEach((item) => {
        const [desc, value] = item.split('<->');
        transaction.push({ name: desc, value });
    });
    return { transaction };
};

// nguồn changeRowBackground: thao tác DOM trực tiếp, gỡ class ở mọi dòng rồi gắn cho dòng được chọn
const changeRowBackground = (rowkey) => {
    const rowSelected = document.getElementsByClassName('selected-row');
    if (rowSelected && rowSelected.length > 0) {
        for (let i = rowSelected.length - 1; i >= 0; i--) {
            rowSelected[i].classList.remove('selected-row');
        }
    }
    const row = document.querySelectorAll(`[data-row-key="${rowkey}"]`);
    if (row && rowkey && row[0]) row[0].classList.add('selected-row');
};

const LogManagementListPage = () => {
    const [detailData, setDetailData] = useState([]);
    const [sideTableData, setSideTableData] = useState([]);
    const [visibleDetail, setVisibleDetail] = useState(false);
    const [visibleInfo, setVisibleInfo] = useState(false);
    const [infoData, setInfoData] = useState({});
    // Khoá dòng đang chọn ở BẢNG CHI TIẾT. Nguồn tô nền bằng thao tác DOM, nhưng ở React 18
    // các cập nhật state được gộp lô nên khi `changeRowBackground` chạy thì bảng CHƯA render
    // -> không tìm thấy `[data-row-key]`, mất highlight dòng đầu. Dùng state + rowClassName
    // cho bảng này; hiệu ứng nhìn thấy giống hệt.
    const [selectedDetailKey, setSelectedDetailKey] = useState(null);
    const infoTitle = useRef('');

    const { execute: executeGetById, loading: loadingGetDetail } = useFetch(apiConfig.logs.getById, {
        immediate: false,
    });

    const { data, setData, mixinFuncs, queryFilter, loading, pagination } = useListBase({
        apiConfig: {
            getList: apiConfig.logs.getList,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE, // nguồn: 50
            objectName: 'Log',
        },
        override: (funcs) => {
            // ⚠️ nguồn getList(): CHỈ gọi API khi có `search.posId`
            // Không có posId (vd. vừa xoá ô input bằng dấu x) -> dọn bảng để "reload"
            // về trạng thái trống, thay vì giữ nguyên dữ liệu tìm kiếm cũ trên màn hình.
            funcs.getList = () => {
                const params = mixinFuncs.prepareGetListParams(queryFilter);
                if (params.posId) mixinFuncs.handleFetchList({ ...params });
                else setData([]);
            };
        },
    });

    // nguồn componentDidMount / componentWillUnmount
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) mixinFuncs.getList();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryFilter]);

    // nguồn handleParseData: dựng bảng món ăn bên phải + tô nền dòng đang chọn
    const handleParseData = (foodString, rowkey) => {
        setSideTableData(parserFood(foodString));
        setSelectedDetailKey(rowkey);
    };

    // nguồn getDetail: getDataById -> getDataDetailMapping = JSON.parse(data.foodData)
    const onShowDetail = (id) => {
        executeGetById({
            pathParams: { id },
            onCompleted: (res) => {
                let parsed = [];
                try {
                    parsed = JSON.parse(res?.data?.foodData);
                } catch (err) {
                    parsed = [];
                }
                setDetailData(parsed);
                setVisibleDetail(true);
                // nguồn: nếu có dòng đầu thì dựng luôn bảng món ăn của dòng đó
                if (parsed && parsed.length !== 0) handleParseData(parsed[0][5], parsed[0][2]);
            },
            onError: (err) => {
                if (err && err.message) showErrorMessage(err.message);
                else showErrorMessage('Cập nhật Log thất bại. Vui lòng thử lại!');
            },
        });
    };

    // nguồn handleShowInfoModal
    const handleShowInfoModal = (dataPayment) => {
        const { otherData, deviceId, total, customerName, tips } = decodeData(dataPayment);
        infoTitle.current = deviceId;
        if (otherData) {
            setInfoData({ ...parseInputString(otherData), total, customerName, tips });
            setVisibleInfo(true);
        } else {
            showErrorMessage('Không có dữ liệu để hiển thị!');
        }
    };

    const columns = [
        {
            title: 'Ngày',
            dataIndex: 'createdDate',
            render: (createdDate) => convertUtcToTimezone(createdDate, 'DD.MM.YYYY HH:mm:ss'),
            width: '200px',
        },
        { title: 'ID', dataIndex: 'posId', width: '100px' },
        {
            title: 'Người thanh toán',
            dataIndex: 'dataPayment',
            render: (dataPayment) => {
                const { deviceId } = decodeData(dataPayment);
                return <>{deviceId ? <span>{deviceId}</span> : ''} </>;
            },
        },
        { title: 'Tên nhân viên', dataIndex: 'employee', width: '200px' },
        {
            title: 'Tổng tiền',
            dataIndex: 'dataPayment',
            width: '150px',
            align: 'center',
            render: (dataPayment) => {
                const { total } = decodeData(dataPayment);
                return total ? (
                    <span>
                        {total} &euro;
                    </span>
                ) : (
                    ''
                );
            },
        },
        {
            title: 'Tiền boa',
            dataIndex: 'dataPayment',
            width: '150px',
            align: 'center',
            render: (dataPayment) => {
                const { tips } = decodeData(dataPayment);
                return renderTips(tips);
            },
        },
        {
            title: 'Đã in',
            dataIndex: 'dataPayment',
            width: '50px',
            render: (dataPayment) => {
                const { isPrint } = decodeData(dataPayment);
                return isPrint ? <CheckSquareOutlined style={{ color: 'green' }} /> : <BorderOutlined />;
            },
        },
        {
            title: 'Hành động',
            width: '100px',
            align: 'center',
            render: (dataRow) => (
                <span>
                    {/* nguồn: actionColumns.isEdit = true, gate [1]+[3]. permissions[3] là chuỗi giả
                        'not_have_update' nên nút này LUÔN ẨN — giữ nguyên, không bỏ khỏi code. */}
                    <span>
                        <ElementWithPermission permissions={getEditPermissions(PAGE_PATH)}>
                            <Button
                                type="link"
                                className="no-padding"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShowDetail(dataRow.id);
                                }}
                            >
                                <EditOutlined />
                            </Button>
                        </ElementWithPermission>
                    </span>
                    {/* nguồn: icon con mắt luôn có, KHÔNG gate quyền */}
                    <span>
                        <Divider type="vertical" />
                        <Button
                            type="link"
                            className="no-padding"
                            onClick={(e) => {
                                e.stopPropagation();
                                onShowDetail(dataRow.id);
                            }}
                        >
                            <EyeOutlined />
                        </Button>
                    </span>
                </span>
            ),
        },
    ];

    // nguồn detailTable: đọc theo CHỈ SỐ CHUỖI của mảng foodData
    const detailColumns = [
        { title: 'STT', dataIndex: '2', width: '50px', align: 'center', render: (index) => <b>{index}</b> },
        {
            title: 'Thời gian',
            dataIndex: '3',
            width: 175,
            render: (date) => convertUtcToTimezone(dayjs(date).format('DD.MM.YYYY HH:mm:ss'), 'DD.MM.YYYY HH:mm:ss'),
        },
        { title: 'Nhân viên', dataIndex: '1' },
        { title: 'Bàn số', dataIndex: '4' },
        {
            title: 'Tổng tiền',
            dataIndex: '6',
            align: 'center',
            width: 150,
            render: (cent) => {
                const total = formatEuro(Number(cent) / 100);
                return total ? (
                    <b>
                        {total} &euro;
                    </b>
                ) : (
                    ''
                );
            },
        },
        {
            title: 'Tiền boa',
            dataIndex: '30',
            align: 'center',
            width: 150,
            render: (cent) => {
                const isEuroText = typeof cent === 'string' && cent.includes(',');
                return renderTips(isEuroText ? cent : Number(cent) / 100);
            },
        },
        // ⚠️ tiêu đề có KHOẢNG TRẮNG THỪA ở cuối — đúng như nguồn
        { title: 'Cách trả ', dataIndex: '7' },
        { title: 'Trong | Ngoài', dataIndex: '8', align: 'center', width: 150 },
        { title: 'Giảm giá', dataIndex: '9', align: 'center', width: 100 },
    ];

    // nguồn sideTable: danh sách món, dòng type !== 1 thụt vào và in nghiêng
    const sideColumns = [
        {
            title: 'Tên',
            dataIndex: 'name',
            width: '175px',
            render: (name, record) =>
                record.type === 1 ? (
                    <span className="detail-name">{name}</span>
                ) : (
                    <i className="detail-name">
                        <CaretRightOutlined /> {name}
                    </i>
                ),
        },
        {
            title: 'Số',
            dataIndex: 'quantity',
            width: '50px',
            render: (quantity, record) => <span> {record.type !== 2 && quantity} </span>,
        },
        {
            title: 'Giá tiền',
            dataIndex: 'price',
            render: (price, record) => <span> {record.price !== '0,00' && price} </span>,
        },
    ];

    // nguồn getSearchFields: một ô duy nhất, placeholder 'ID'
    const searchFields = [{ key: 'posId', seachPlaceholder: 'ID' }];

    return (
        <PageWrapper routes={[{ breadcrumbName: 'Quản lí đơn hàng' }]}>
            <Loading show={loadingGetDetail} white />
            <ListPage
                searchForm={
                    // nguồn override renderSearchForm: dùng SearchFormLog RIÊNG, không phải SearchForm chung
                    <SearchFormLog
                        searchFields={searchFields}
                        initialValues={queryFilter}
                        allowClear={true}
                        onSubmit={(values) => mixinFuncs.changeFilter(values)}
                        refreshTable={() => mixinFuncs.getList()}
                        handleClear={(input) => {
                            // nguồn handleClear: xoá trắng ô -> về /logs và dọn dữ liệu đang hiển thị
                            if (input === '') mixinFuncs.changeFilter({});
                        }}
                    />
                }
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey={(record) => record.id}
                        pagination={{
                            ...pagination,
                            // nguồn: showTotal in đậm
                            showTotal: (total, range) => (
                                <b>
                                    {range[0]}-{range[1]} của {total} dòng
                                </b>
                            ),
                        }}
                        onRow={(record) => ({
                            onClick: () => {
                                handleShowInfoModal(record.dataPayment);
                                changeRowBackground(record.id);
                            },
                        })}
                    />
                }
            />

            {/* Modal chi tiết đơn hàng: hai bảng cạnh nhau */}
            <LicenseModal
                visible={visibleDetail}
                title="Chi tiết đơn hàng"
                onCancel={() => {
                    setVisibleDetail(false);
                    setSideTableData([]);
                    setSelectedDetailKey(null);
                }}
                width={1400}
                className="logs-modal"
                emptyFooter
            >
                <div className="logs-wrapper">
                    <LogTable
                        columns={detailColumns}
                        rowKey={(record) => record[2]}
                        dataSource={detailData}
                        className="base-table logs-table"
                        rowClassName={(record) => (record[2] === selectedDetailKey ? 'selected-row' : '')}
                        onRow={(record) => ({
                            onClick: () => handleParseData(record['5'], record[2]),
                        })}
                    />
                    <LogTable
                        columns={sideColumns}
                        rowKey={(record) => record.id}
                        dataSource={sideTableData}
                        className="base-table logs-table"
                    />
                </div>
            </LicenseModal>

            {/* Modal thông tin thanh toán (bấm vào dòng) */}
            <LicenseModal
                visible={visibleInfo}
                title={infoTitle.current || ''}
                onCancel={() => {
                    setVisibleInfo(false);
                    infoTitle.current = '';
                    changeRowBackground(null);
                }}
                width={400}
                top={125}
                className="info-modal"
                emptyFooter
            >
                <div className={styles.info}>
                    <h3>
                        TỔNG TIỀN {'['}
                        {infoData.customerName || ''}
                        {']'}
                    </h3>
                    <h1>{infoData.total || ''} &euro;</h1>
                    {infoData.tips ? (
                        <div className={styles.tips}>
                            Tiền boa: {formatEuro(infoData.tips) || infoData.tips} &euro;
                        </div>
                    ) : null}
                    <div className={styles.content}>
                        {infoData.transaction &&
                            infoData.transaction.map((item, index) => (
                                <div key={index} className={styles.item}>
                                    <div className={styles.header}>
                                        <div className={styles.name}>{item.name}</div>
                                        <div className={styles.name}></div>
                                    </div>
                                    <h2 className={styles.price}>{item.value} &euro;</h2>
                                </div>
                            ))}
                    </div>
                </div>
            </LicenseModal>
        </PageWrapper>
    );
};

export default LogManagementListPage;
